import { useEffect, useRef } from "react";
import { parseDurationToMs } from "../lib/ytPlayer";

// useYouTubeAutoAdvance
// =====================
// Shared "in-app YouTube iframe" auto-advance + auto-unmute detector,
// extracted VERBATIM from InAppVideoPlayer's battle-hardened effect so
// both the video and movie players use one implementation. The video
// player is the SUPERSET (catalog-duration hard timer, wall-clock
// playhead, near-end + watchdog fallbacks, played-gate) because mobile
// YouTube frequently DROPS state/position events — those fallbacks are
// the guaranteed continuous-play mechanism. The movie player opts out of
// the wall-clock/catalog/watchdog pieces via `options` so its behavior
// is unchanged.
//
// Returns the refs the host still reads outside the effect (resume
// position, lock-screen progress, Pin-to-Trending playhead).
export default function useYouTubeAutoAdvance({
  iframeRef,
  active,                 // open && stage === "playing"
  trackKey,               // item?.id — re-arms the listener per track
  durationText,           // catalog duration string ("4 min" / "3:42")
  bucket,
  onAdvance,              // () => void  — advance to the next item (called once)
  onUnmute,               // () => void  — we detected/forced unmuted state
  onError,                // () => void  — YouTube reported the video unavailable
  onDuration,             // (seconds) => void — YT reported a real duration
  iframeLoadedAtRef = null,
  options = {},
}) {
  const {
    catalogTimer = false,
    wallClock = false,
    watchdog = false,
    playedGate = false,
    advanceOnError = false,
  } = options;

  const ytTimeRef = useRef({ position: 0, duration: 0 });
  const playheadRef = useRef(0);
  const playedRef = useRef(false);

  // Keep callbacks fresh without re-arming the listener every render.
  const cbRef = useRef({ onAdvance, onUnmute, onError, onDuration });
  cbRef.current = { onAdvance, onUnmute, onError, onDuration };

  useEffect(() => {
    if (!active) return undefined;
    let advancedNearEnd = false;
    playedRef.current = false;
    ytTimeRef.current = { position: 0, duration: 0 };
    playheadRef.current = 0;
    if (iframeLoadedAtRef) iframeLoadedAtRef.current = 0;

    let lastPlayingAt = performance.now();
    let elapsedAtPlayingStart = 0;
    let readySynced = false;
    const effectArmedAt = performance.now();

    const advanceOnce = () => {
      if (advancedNearEnd) return;
      advancedNearEnd = true;
      cbRef.current.onAdvance && cbRef.current.onAdvance();
    };

    // ── Catalog-duration hard timer (video only) ──────────────────────
    const catalogDurationMs = parseDurationToMs(durationText);
    let catalogTimerId = null;
    if (catalogTimer && catalogDurationMs > 0) {
      catalogTimerId = setTimeout(() => advanceOnce(), catalogDurationMs + 4000);
    }

    const localElapsed = () => {
      if (!lastPlayingAt) return ytTimeRef.current.position;
      const wc = (performance.now() - lastPlayingAt) / 1000;
      return Math.max(ytTimeRef.current.position, elapsedAtPlayingStart + wc);
    };

    let unmuted = false;
    const sendUnmute = () => {
      const fr = iframeRef.current;
      if (!fr || !fr.contentWindow) return;
      try {
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "unMute", args: [] }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "setVolume", args: [100] }), "*");
      } catch (_) { /* noop */ }
    };

    const handler = (e) => {
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      const data = typeof e.data === "string" ? (() => { try { return JSON.parse(e.data); } catch { return null; } })() : e.data;
      if (!data || typeof data !== "object") return;

      if (data.event === "onReady") {
        if (wallClock && !readySynced) {
          lastPlayingAt = performance.now();
          elapsedAtPlayingStart = 0;
          readySynced = true;
        }
        sendUnmute();
        unmuted = true;
        cbRef.current.onUnmute && cbRef.current.onUnmute();
      }
      if (data.event === "infoDelivery" && data.info?.muted === false) {
        unmuted = true;
        cbRef.current.onUnmute && cbRef.current.onUnmute();
      }

      const info = data.info || data;
      if (typeof info.currentTime === "number") {
        ytTimeRef.current.position = info.currentTime;
        if (info.currentTime > 0) {
          playedRef.current = true;
          if (wallClock) {
            lastPlayingAt = performance.now();
            elapsedAtPlayingStart = info.currentTime;
          }
        }
      }
      if (typeof info.duration === "number" && info.duration > 0) {
        ytTimeRef.current.duration = info.duration;
        cbRef.current.onDuration && cbRef.current.onDuration(info.duration);
      }

      // Near-end fallback (message-handler side).
      const { position: pos, duration: dur } = ytTimeRef.current;
      const gate = playedGate ? playedRef.current : true;
      if (!advancedNearEnd && gate && dur > 5 && pos > 0 && pos >= dur - 0.6) {
        advanceOnce();
      }

      if (data.event === "onError" || data.info?.errorCode != null) {
        cbRef.current.onError && cbRef.current.onError();
        if (advanceOnError && !advancedNearEnd) {
          advancedNearEnd = true;
          setTimeout(() => cbRef.current.onAdvance && cbRef.current.onAdvance(), 1200);
        }
      }

      if (data.event === "onStateChange" || data.info?.playerState != null) {
        const state = data.info?.playerState ?? data.info;
        if (state === 1) {
          playedRef.current = true;
          if (!unmuted) sendUnmute();
          if (wallClock) {
            lastPlayingAt = performance.now();
            elapsedAtPlayingStart = ytTimeRef.current.position;
          }
        }
        if (state === 0) advanceOnce();
      }
    };
    window.addEventListener("message", handler);

    const sub = setTimeout(() => {
      const fr = iframeRef.current;
      if (!fr || !fr.contentWindow) return;
      try {
        fr.contentWindow.postMessage(JSON.stringify({ event: "listening", id: trackKey || "player" }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onReady"] }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onError"] }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "getDuration", args: [] }), "*");
      } catch (_) {}
      sendUnmute();
    }, 600);
    const t1 = setTimeout(() => { if (!unmuted) sendUnmute(); }, 1500);
    const t2 = setTimeout(() => { if (!unmuted) sendUnmute(); }, 3000);
    const t3 = setTimeout(() => { if (!unmuted) sendUnmute(); }, 5000);

    const pollId = setInterval(() => {
      const fr = iframeRef.current;
      if (!fr || !fr.contentWindow) return;
      if (wallClock && !readySynced && iframeLoadedAtRef && iframeLoadedAtRef.current && iframeLoadedAtRef.current > effectArmedAt) {
        lastPlayingAt = iframeLoadedAtRef.current;
        elapsedAtPlayingStart = 0;
        readySynced = true;
      }
      try {
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "getCurrentTime", args: [] }), "*");
        if (!ytTimeRef.current.duration) {
          fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "getDuration", args: [] }), "*");
        }
      } catch (_) {}
      const dur = ytTimeRef.current.duration;
      const pos = wallClock ? localElapsed() : ytTimeRef.current.position;
      if (wallClock) playheadRef.current = pos;
      const gate = playedGate ? playedRef.current : true;
      if (!advancedNearEnd && gate && dur > 5 && pos > 0 && pos >= dur - 1.0) {
        advanceOnce();
      }
      if (watchdog && !advancedNearEnd && gate && dur > 5 && pos > dur + 8.0) {
        advanceOnce();
      }
    }, 500);

    return () => {
      window.removeEventListener("message", handler);
      clearTimeout(sub);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(pollId);
      if (catalogTimerId) clearTimeout(catalogTimerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, trackKey]);

  return { ytTimeRef, playheadRef, playedRef };
}
